import React, { Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// Packages
import { Button, Popconfirm, Space, Spin, Table, Tooltip, Typography } from 'antd';
import { DeleteFilled, DownloadOutlined, EditFilled, EyeFilled, QuestionCircleFilled } from '@ant-design/icons';
import PropTypes from 'prop-types';

// Actions
import { download_file, file_delete, get_all_files, set_file_id } from '../../actions/filesActions';

const FilesTable = (props) => {
	const { messageApi, orgFilters, areaFilters, setIsModalOpen, setUpdate, setFileToUpdate, pageSize, skip, setSkip } = props;

	const navigate = useNavigate();

	const dispatch = useDispatch();

	const { isAdminUser } = useSelector(state => state.auth);

	const { user_staff } = useSelector(state => state.staffs);

	const { global_data } = useSelector(state => state.global);

	const { all_files_loading, all_files, file_delete_loading, file_download_loading, files_errors } = useSelector(state => state.trainingFiles);

	const download = (record) => {
		if (record?.filename === null) {
			messageApi.warning(global_data?.files?.downloadWarning, 4);
			return;
		}
		
		const lastDotIndex = record?.filename.lastIndexOf(".");
		// Gets the extension with the dot (eg: ".csv")
		const extension = record?.filename.slice(lastDotIndex);
	
		const finalFileName = `${record?.name}${extension}`;
	
		dispatch(download_file(messageApi, global_data, record?._id?.$oid, finalFileName));
	};	
	
	const update = (record) => {
		setIsModalOpen(true);
		setUpdate(true);
		setFileToUpdate(record);
		dispatch(set_file_id(record._id.$oid));
	}

	const deleteFile = (record) => {
		let newSkip = skip;

		if ((all_files.count - 1) % pageSize === 0) {
			newSkip -= pageSize;
			setSkip(newSkip);
		}

		const filters = { ...orgFilters, ...areaFilters, skip: newSkip, limit: pageSize };
		dispatch(file_delete(messageApi, global_data, record._id.$oid, filters));
	}

	const columns = [
		...(orgFilters.organization === ''
			? [
					{
						title: global_data?.organizationTerms?.organization,
						dataIndex: 'organization',
						key: 'organization',
						render: (organization) => (
							organization
								?	<Typography.Text copyable={{ text: organization?._id?.$oid, tooltips: [global_data?.utils?.copyId, global_data?.utils?.idCopied] }}>
										{organization?.name}
									</Typography.Text>
								: '-'
						)
					}
				]
			: []
		),
		...(orgFilters.workspace === ''
			? [
					{
						title: global_data?.organizationTerms?.workspace,
						dataIndex: 'workspace',
						key: 'workspace',
						render: (workspace) => (
							workspace
								?	<Typography.Text copyable={{ text: workspace?._id?.$oid, tooltips: [global_data?.utils?.copyId, global_data?.utils?.idCopied] }}>
										{workspace?.name}
									</Typography.Text>
								: '-'
						)
					}
				]
			: []
		),
		...(areaFilters.region === ''
			? [
					{
						title: global_data?.organizationTerms?.region,
						dataIndex: 'region',
						key: 'region',
						render: (region) => (
							region
								?	<Typography.Text copyable={{ text: region?._id?.$oid, tooltips: [global_data?.utils?.copyId, global_data?.utils?.idCopied] }}>
										{region?.name}
									</Typography.Text>
								: '-'
						)
					}
				]
			: []
		),
		...(areaFilters.zone === ''
			? [
					{
						title: global_data?.organizationTerms?.zone,
						dataIndex: 'zone',
						key: 'zone',
						render: (zone) => (
							zone
								?	<Typography.Text copyable={{ text: zone?._id?.$oid, tooltips: [global_data?.utils?.copyId, global_data?.utils?.idCopied] }}>
										{zone?.name}
									</Typography.Text>
								: '-'
						)
					}
				]
			: []
		),
		{
			title: global_data?.files?.formInputName,
			dataIndex: 'name',
			key: 'name',
			render: (_, record) => (
				<Typography.Text copyable={{ text: record?._id?.$oid, tooltips: [global_data?.utils?.copyId, global_data?.utils?.idCopied] }}>
					{record?.name}
				</Typography.Text>
			)
		},
		{
			title: global_data?.files?.formInputDescription,
			dataIndex: 'description',
			key: 'description'
		},
		{
			title: global_data?.table?.actionHeader,
			key: 'action',
			render: (_, record) => (
				<Space>
					{(isAdminUser || user_staff?.staff_type === 1) && (
						<Tooltip title='Logs'>
							<Button
								color='gold'
								variant='solid'
								type='primary'
								shape='circle'
								size='large'
								icon={<EyeFilled />}
								onClick={() => navigate(`/files/${record?._id?.$oid}/logs`)}
							/>
						</Tooltip>
					)}
					<Tooltip title={global_data?.actionButtons?.download}>
						<Button
							color='green'
							variant='solid'
							type='primary'
							shape='circle'
							size='large'
							disabled={record?.filename === null}
							loading={!!file_download_loading[record?._id?.$oid]}
							icon={<DownloadOutlined />}
							onClick={() => download(record)}
						/>
					</Tooltip>
					{(isAdminUser || user_staff?.staff_type === 1) && (
						<Fragment>
							<Tooltip title={global_data?.actionButtons?.edit}>
								<Button
									type='primary'
									shape='circle'
									size='large'
									icon={<EditFilled />}
									onClick={() => update(record)}
								/>
							</Tooltip>
							<Popconfirm
								title={global_data?.files?.deleteConfirmTitle}
								description={global_data?.files?.deleteConfirmText}
								icon={<QuestionCircleFilled style={{ color: '#1677FF' }} />}
								onConfirm={() => deleteFile(record)}
								okButtonProps={{
									loading: file_delete_loading
								}}
								okText={global_data?.actionButtons?.confirm}
								cancelText={global_data?.actionButtons?.cancel}
							>
								<Button
									type='primary'
									shape='circle'
									size='large'
									danger
									icon={<DeleteFilled />}
								/>
							</Popconfirm>
						</Fragment>
					)}
				</Space>
			)
		}
	];

	const handleTableChange = (pagination, filters, sorter) => {
		const skip = (pagination.current - 1) * pageSize;
		setSkip(skip);

		const filesFilters = { ...orgFilters, ...areaFilters, skip: skip, limit: pageSize };
		dispatch(get_all_files(filesFilters));
	};

	return (
		<div className='mt-5'>
			<Spin spinning={all_files_loading} tip={global_data?.utils?.loading} size='large'>
				<Table
					columns={columns}
					dataSource={all_files.files}
					onChange={handleTableChange}
					scroll={{ x: 'auto' }}
					pagination={{
						pageSize: pageSize,
						total: all_files.count,
						showTotal: (total, range) => `${range[0]} - ${range[1]} ${global_data?.table?.paginationOf} ${total}`,
						hideOnSinglePage: true,
						current: (skip / pageSize) + 1
					}}
				/>
			</Spin>
		</div>
	)
}

FilesTable.propTypes = {
	messageApi: PropTypes.object.isRequired,
	orgFilters: PropTypes.object.isRequired,
	areaFilters: PropTypes.object.isRequired,
	setIsModalOpen: PropTypes.func.isRequired,
	setUpdate: PropTypes.func.isRequired,
	setFileToUpdate: PropTypes.func.isRequired,
	pageSize: PropTypes.number.isRequired,
	skip: PropTypes.number.isRequired,
	setSkip: PropTypes.func.isRequired
}

export default FilesTable;
